// Page publique de suivi personnel : une personne qui a soumis une demande d'accréditation
// revient ici avec son lien de suivi (reçu à la soumission, cf. AccreditationPublicFormPage)
// pour vérifier où en est SA seule demande. Aucune authentification, lecture seule.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Paper, Box, Stack, Typography, Chip, Alert, CircularProgress } from '@mui/material';
import { getAccreditationTrackingStatus } from '../../api/api';
import wiloutLogo from '../../images/wilout-logo-full.svg';
import { STATUS_META, BRAND_GRADIENT } from './formVisuals';

const Header = () => (
  <Box sx={{ background: BRAND_GRADIENT }}>
    <Container sx={{ maxWidth: '480px !important', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 64 }}>
      <Box sx={{ bgcolor: '#fff', borderRadius: '10px', px: 1.25, py: 0.75, display: 'flex', alignItems: 'center' }}>
        <img src={wiloutLogo} alt="Wilout" style={{ height: 20 }} />
      </Box>
    </Container>
  </Box>
);

const PageShell = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
    <Header />
    {children}
  </Box>
);

const AccreditationStatusPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAccreditationTrackingStatus(token);
        if (!cancelled) setStatus(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'Ce lien de suivi est invalide.'
            : 'Erreur lors du chargement du statut.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <PageShell>
        <Container sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <Container sx={{ py: 8, maxWidth: '480px !important' }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </PageShell>
    );
  }

  const meta = STATUS_META[status.status] || STATUS_META.pending;

  return (
    <PageShell>
      <Container sx={{ py: 8, maxWidth: '480px !important' }}>
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {status.projectName}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            {status.formLabel}
          </Typography>

          <Chip
            label={meta.label}
            sx={{ bgcolor: meta.bg, color: meta.fg, fontWeight: 700, fontSize: 14, height: 32, px: 1 }}
          />

          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {[status.prenom, status.nom].filter(Boolean).join(' ')}
            </Typography>
            {status.submittedAt && (
              <Typography variant="caption" color="text.secondary">
                Demande soumise le {new Date(status.submittedAt).toLocaleDateString('fr-FR')}
              </Typography>
            )}
            {status.decidedAt && (
              <Typography variant="caption" color="text.secondary">
                Décision prise le {new Date(status.decidedAt).toLocaleDateString('fr-FR')}
              </Typography>
            )}
          </Stack>
        </Paper>
      </Container>
    </PageShell>
  );
};

export default AccreditationStatusPage;
